import { useUser } from "@modules/auth/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { DEFAULT_MAXIMUM_AMOUNT } from "@modules/currency/currency"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { ProgramBundleFormFirstPage } from "@modules/programBundle/components/programBundleFormFirstPage"
import { ProgramBundleFormSecondPage } from "@modules/programBundle/components/programBundleFormSecondPage"
import { ProgramBundleFormThirdPage } from "@modules/programBundle/components/programBundleThirdPage"
import { ProgramBundleDraftEntity, ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import {
  getValidationProgramBundleSchema,
  ProgramBundleFormInputKey,
} from "@modules/programBundle/formik/programBundleFormik"
import { useProgramBundleEdition, useProgramBundlesCreation } from "@modules/programBundle/hooks/useProgramBundle"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useToast } from "@modules/ui/components/huToast"
import { SimpleFormik } from "@src/typings/formik"
import { useFormik } from "formik"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export type ProgramBundleFormPageProps = {
  isFormFieldInValid: (name: ProgramBundleFormInputKey) => boolean
  getFormErrorMessage: (name: ProgramBundleFormInputKey, errorMessage?: string) => React.JSX.Element | undefined
  formik: SimpleFormik<ProgramBundleDraftEntity, ProgramBundleFormInputKey>
  setPageNumber: React.Dispatch<React.SetStateAction<number>>
  handleSubmit: () => void
  isSubmitting: boolean
  isEdited: boolean
}

export type ProgramBundleFormProps = {
  programBundle?: ProgramBundleEntity
} & React.HTMLAttributes<HTMLDivElement>

export const ProgramBundleForm: React.FC<ProgramBundleFormProps> = ({ programBundle, ...props }) => {
  const [pageNumber, setPageNumber] = useState(1)
  const [loading, setLoading] = useState(false)
  const toast = useToast()
  const containerRef = useRef<HTMLFormElement>(null)
  const { createProgramBundle } = useProgramBundlesCreation()
  const { updateProgramBundle } = useProgramBundleEdition(programBundle?.id)
  const { navigateToProgramBundleResume, hideSidebar } = useProgramBundleSidebar()
  const user = useUser()
  const maxPrice = user?.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT
  const minPrice = user?.stripeMinimumAmount ?? 0

  const programBundleDraft = programBundle
    ? ProgramBundleDraftEntity.fromProgramBundleEntity(programBundle)
    : ProgramBundleDraftEntity.createDraft()

  const isEdited = !!programBundle

  const formik = useFormik<ProgramBundleDraftEntity>({
    initialValues: programBundleDraft,
    validationSchema: getValidationProgramBundleSchema(pageNumber, maxPrice, minPrice),
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        if (isEdited) {
          updateProgramBundle(values)

          toast.show({
            severity: "success",
            summary: word("programBundle.form.create.edited"),
          })
          hideSidebar(false)
          await new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
            navigateToProgramBundleResume(programBundle.id, true)
          })
        } else {
          const result = await createProgramBundle(values)
          toast.show({
            severity: "success",
            summary: word("programBundle.form.create.success"),
          })
          hideSidebar(false)
          await new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
            navigateToProgramBundleResume(result.id, true)
          })
        }
      } catch (e) {
        toast.show({
          severity: "error",
          summary: word("programBundle.form.create.error"),
        })
      } finally {
        setLoading(false)
      }
    },
  })

  //Workaround for this issue: https://github.com/jaredpalmer/formik/issues/3151
  const nextPage = useCallback(async () => {
    if ([1, 2, 3].includes(pageNumber)) {
      Object.keys(formik.values).forEach((inputKey) => {
        formik.setFieldTouched(inputKey)
      })
      await formik.validateForm().then((value) => {
        if (Object.values(value).length === 0) {
          if (pageNumber === 3) {
            formik.handleSubmit()
          } else {
            setPageNumber((prevState) => prevState + 1)
          }
        } else {
          const errorElements = containerRef.current?.getElementsByClassName("p-error")
          if (errorElements && errorElements.length > 0) {
            document
              .getElementById("sidebar-content")
              ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop, behavior: "smooth" })
          }
        }
      })
    }
  }, [formik, pageNumber, containerRef])

  const formProps = useMemo(
    () => ({
      formik: {
        values: formik.values,
        initialValues: formik.initialValues,
        getFieldProps: formik.getFieldProps,
        setFieldValue: formik.setFieldValue,
        validateField: formik.validateField,
      },
      isSubmitting: loading,
      isEdited,
      handleSubmit: nextPage,
      setPageNumber,
      isFormFieldInValid: (input: ProgramBundleFormInputKey) => isFormFieldInValid(input, formik),
      getFormErrorMessage: (input: ProgramBundleFormInputKey, errorMessage?: string) =>
        getFormErrorMessage(input, formik, { errorMessage }),
    }),
    [formik, nextPage, loading, isEdited],
  )

  useEffect(() => {
    document.getElementById("sidebar-content")?.scrollTo({ top: 0 })
  }, [pageNumber])

  return (
    <div {...props}>
      <form onSubmit={formik.handleSubmit} ref={containerRef}>
        {pageNumber === 1 && <ProgramBundleFormFirstPage {...formProps} />}
        {pageNumber === 2 && <ProgramBundleFormSecondPage {...formProps} />}
        {pageNumber === 3 && <ProgramBundleFormThirdPage {...formProps} />}
      </form>
    </div>
  )
}
